// Motor de cálculo do parcelamento com juros embutidos (chamado #5686).
//
// Fonte única da aritmética do acordo. Vive separado da tela porque é dinheiro:
// precisa rodar sob `node --test` sem React, sem Supabase e sem relógio do
// navegador. A tela, o termo e o boleto leem TODOS daqui — três contas
// diferentes para o mesmo acordo é como nascem as divergências que o cliente
// descobre antes da gente.
//
// Modelo, validado contra os dois anexos do chamado:
//
//   1. Dívida bruta = faturas selecionadas + encargos de mora (opcionais)
//                     + saldo de energia negociado (opcional, contrato tipo B)
//                     + outros débitos fora do sistema (opcionais, sem mora)
//   2. Desconto (opcional) sai da dívida bruta inteira; o que sobra é a dívida
//      negociada. Sem desconto, as duas são o mesmo número.
//   3. Entrada é abatida da dívida negociada e NÃO sofre juros de financiamento
//      (o chamado diz "juros com base na taxa do analista do que sobrar da
//      entrada").
//   4. O que sobra é financiado em tabela Price: parcela constante, juros
//      sobre saldo devedor decrescente.
//
// Conferência contra a tela de negociação da Equatorial anexada ao chamado
// (dívida 10.019,43 / entrada 25% / 7x / 1% a.m.): a soma da coluna "Débito"
// das 7 parcelas dá 7.514,57 — exatamente dívida − entrada — e os juros de
// financiamento somam 299,03, igual à linha de total da tela. É Price sobre o
// saldo pós-entrada. A calculadora XLSX anexada faz a mesma conta com PMT.
//
// TUDO aqui trabalha em CENTAVOS INTEIROS. Ponto flutuante em dinheiro erra no
// terceiro acordo: 0.1 + 0.2 !== 0.3, e um acordo de 24x acumula essa sujeira
// 24 vezes antes de virar boleto. A fronteira com o mundo (banco, tela) é
// reais; o miolo é int.

/** Teto de parcelas no modo "por valor da parcela". Acima disso a parcela é
 *  pequena demais para o juro — o acordo nunca fecharia. */
const MAX_PARCELAS = 360;

export interface FaturaParaAcordo {
  id: string | number;
  /** Valor devido hoje, em reais. */
  valor: number;
  /** ISO 'YYYY-MM-DD'. Null = não vencida (não gera mora). */
  vencimento: string | null;
}

export interface EncargosMora {
  /** Multa única sobre o valor vencido, em % (ex.: 2 = 2%). */
  multaPercent: number;
  /** Juros de mora ao mês, em % (ex.: 1 = 1% a.m.), pro rata die. */
  moraMensalPercent: number;
}

export interface SaldoNegociado {
  /** kWh do saldo do cliente que entram na composição. */
  kwh: number;
  /** Tarifa R$/kWh definida pelo analista. */
  tarifa: number;
  /** Desconto contratual aplicado sobre o bruto, em % (ex.: 18 = 18%). */
  descontoPercent: number;
}

/** Débito que não existe no sistema como fatura (multa de distrato, taxa...)
 *  e entra no acordo pelo valor informado. Pedido de 08/10/2026. */
export interface OutroDebito {
  /** O que é. Vai impresso numa linha do Anexo A do termo. */
  descricao: string;
  /** Valor em reais. */
  valor: number;
}

export type ModoParcelamento =
  | { modo: 'por_numero'; numeroParcelas: number }
  | { modo: 'por_valor'; valorParcela: number };

export interface EntradaConfig {
  /** Entrada em reais. Tem precedência sobre `percentual` quando ambos vêm. */
  valor?: number;
  /** Entrada em % da dívida negociada (ex.: 25 = 25%). */
  percentual?: number;
}

export interface DescontoConfig {
  /** Desconto em reais. Tem precedência sobre `percentual` quando ambos vêm. */
  valor?: number;
  /** Desconto em % da dívida bruta (ex.: 10 = 10%). */
  percentual?: number;
}

export interface ParametrosAcordo {
  faturas: FaturaParaAcordo[];
  /** Null/ausente = não cobrar mora nem multa (o default do modal). */
  encargos?: EncargosMora | null;
  /** Null/ausente = contrato tipo A (só dívida de faturas). */
  saldo?: SaldoNegociado | null;
  /** Débitos fora do sistema somados à dívida. Não geram multa nem mora: não
   *  têm vencimento aqui, e o valor informado já é o devido. Null/ausente = nenhum. */
  outrosDebitos?: OutroDebito[] | null;
  /** Desconto sobre a dívida bruta inteira (faturas + encargos + saldo +
   *  outros débitos). Null/ausente = sem desconto. */
  desconto?: DescontoConfig | null;
  entrada: EntradaConfig;
  /** Taxa de juros de financiamento ao mês, em % (ex.: 1 = 1% a.m.). */
  taxaJurosMensalPercent: number;
  parcelamento: ModoParcelamento;
  /** Data de referência para a mora pro rata die. ISO 'YYYY-MM-DD'. */
  dataBase: string;
  /** Vencimento da 1ª parcela (a entrada tem vencimento próprio). ISO. */
  primeiroVencimento: string;
  /** Vencimento do boleto da entrada. ISO. */
  vencimentoEntrada: string;
}

export interface DetalheFatura {
  id: string | number;
  valorCentavos: number;
  diasAtraso: number;
  multaCentavos: number;
  moraCentavos: number;
  totalCentavos: number;
}

export interface DetalheOutroDebito {
  descricao: string;
  valorCentavos: number;
}

export interface LinhaParcela {
  numeroParcela: number;
  /** Saldo devedor no início do mês. */
  saldoInicialCentavos: number;
  jurosCentavos: number;
  amortizacaoCentavos: number;
  saldoFinalCentavos: number;
  valorCentavos: number;
  dataVencimento: string;
  mesReferencia: string;
}

export interface ResultadoAcordo {
  detalheFaturas: DetalheFatura[];
  valorFaturasCentavos: number;
  multaTotalCentavos: number;
  moraTotalCentavos: number;
  valorSaldoBrutoCentavos: number;
  descontoSaldoCentavos: number;
  valorSaldoLiquidoCentavos: number;
  detalheOutrosDebitos: DetalheOutroDebito[];
  outrosDebitosCentavos: number;
  /** Faturas + encargos + saldo + outros débitos. É o "valor confessado" do termo. */
  dividaBrutaCentavos: number;
  /** Desconto dado sobre a dívida bruta. Zero quando não houve. */
  descontoDividaCentavos: number;
  /** Percentual efetivo do desconto sobre a dívida bruta. */
  descontoDividaPercentual: number;
  /** Dívida bruta − desconto. É a base da entrada e do financiamento. */
  dividaNegociadaCentavos: number;
  entradaCentavos: number;
  /** Percentual efetivo da entrada sobre a dívida negociada. */
  entradaPercentual: number;
  vencimentoEntrada: string;
  /** Dívida negociada − entrada. É o que sofre juros. */
  valorFinanciadoCentavos: number;
  taxaJurosMensalPercent: number;
  parcelas: LinhaParcela[];
  numeroParcelas: number;
  jurosTotalCentavos: number;
  /** Entrada + soma das parcelas. */
  totalAcordoCentavos: number;
  /** totalAcordo − dívida negociada. O custo do financiamento para o cliente. */
  custoFinanciamentoCentavos: number;
}

export class ErroCalculoAcordo extends Error {}

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

/** Reais → centavos, com meio centavo sempre para cima.
 *
 *  `Math.round(reais * 100)` sozinho NÃO basta e o teste pega: 8.075 * 100 dá
 *  807.4999999999999 em ponto flutuante, então arredonda para 807 — um centavo
 *  comido em silêncio. `toFixed(2)` erra igual pelo mesmo motivo.
 *
 *  `toPrecision(12)` limpa o lixo binário antes do arredondamento (807.5) sem
 *  chegar perto dos 15 dígitos significativos em que um double perde precisão
 *  de verdade — sobra folga para qualquer valor que a Vigor vá cobrar. */
export function paraCentavos(reais: number): number {
  if (!Number.isFinite(reais)) return 0;
  return Math.round(parseFloat((reais * 100).toPrecision(12)));
}

export function paraReais(centavos: number): number {
  return centavos / 100;
}

export function formatarBRL(centavos: number): string {
  return (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Dias corridos entre duas datas ISO. Negativo quando `ate` é anterior.
 *  Usa UTC de propósito: `new Date('2026-09-15')` já é UTC e comparar com um
 *  Date local embaralha o resultado no fuso -03. */
export function diasEntre(de: string, ate: string): number {
  const a = Date.parse(`${de.slice(0, 10)}T00:00:00Z`);
  const b = Date.parse(`${ate.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.round((b - a) / 86400000);
}

/** Soma meses a uma data ISO preservando o dia; dia 31 em mês de 30 cai no
 *  último dia do mês (mesma regra que o modal já usava para os vencimentos). */
export function somarMeses(dataIso: string, meses: number): string {
  const [ano, mes, dia] = dataIso.slice(0, 10).split('-').map(Number);
  const alvo = new Date(Date.UTC(ano, mes - 1 + meses, 1));
  const ultimoDia = new Date(Date.UTC(alvo.getUTCFullYear(), alvo.getUTCMonth() + 1, 0)).getUTCDate();
  const diaFinal = Math.min(dia, ultimoDia);
  const y = alvo.getUTCFullYear();
  const m = String(alvo.getUTCMonth() + 1).padStart(2, '0');
  const d = String(diaFinal).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function mesReferenciaDe(dataIso: string): string {
  const [ano, mes] = dataIso.slice(0, 10).split('-').map(Number);
  return `${MESES[mes - 1]}/${ano}`;
}

/**
 * Parcela da tabela Price, em centavos.
 *
 * PMT = PV · i / (1 − (1+i)^−n). Com i = 0 vira divisão simples — e esse caso
 * acontece de verdade: acordo sem juros é o default de quem só quer dividir a
 * dívida, e a fórmula geral divide por zero ali.
 */
export function parcelaPrice(valorFinanciadoCentavos: number, taxaMensal: number, n: number): number {
  if (n <= 0) throw new ErroCalculoAcordo('O número de parcelas deve ser maior que zero.');
  if (valorFinanciadoCentavos <= 0) return 0;
  if (taxaMensal === 0) return Math.round(valorFinanciadoCentavos / n);
  const fator = taxaMensal / (1 - Math.pow(1 + taxaMensal, -n));
  return Math.round(valorFinanciadoCentavos * fator);
}

/**
 * Quantas parcelas de `valorParcelaCentavos` quitam o saldo.
 *
 * Trava obrigatória: se a parcela não cobre nem o juro do 1º mês, o saldo
 * devedor CRESCE todo mês e o acordo nunca fecha. Sem esta checagem a planilha
 * do analista mostraria 360 linhas e ele acharia que é só "um acordo longo".
 */
export function numeroParcelasPorValor(
  valorFinanciadoCentavos: number,
  taxaMensal: number,
  valorParcelaCentavos: number,
): number {
  if (valorParcelaCentavos <= 0) throw new ErroCalculoAcordo('O valor da parcela deve ser maior que zero.');
  if (valorFinanciadoCentavos <= 0) return 0;
  if (taxaMensal === 0) return Math.ceil(valorFinanciadoCentavos / valorParcelaCentavos);

  const juroPrimeiroMes = valorFinanciadoCentavos * taxaMensal;
  if (valorParcelaCentavos <= juroPrimeiroMes) {
    throw new ErroCalculoAcordo(
      `A parcela não cobre nem os juros do primeiro mês (${formatarBRL(Math.ceil(juroPrimeiroMes))}). ` +
      'Com esse valor a dívida nunca seria quitada — aumente a parcela, aumente a entrada ou reduza a taxa.',
    );
  }

  // n = −log(1 − PV·i/PMT) / log(1+i)
  const n = -Math.log(1 - (valorFinanciadoCentavos * taxaMensal) / valorParcelaCentavos) / Math.log(1 + taxaMensal);
  const arredondado = Math.ceil(n - 1e-9);
  if (arredondado > MAX_PARCELAS) {
    throw new ErroCalculoAcordo(
      `Esse valor de parcela exigiria ${arredondado} meses (limite ${MAX_PARCELAS}). Aumente a parcela.`,
    );
  }
  return Math.max(1, arredondado);
}

/**
 * Tabela de amortização. A última parcela quita o saldo exatamente — mesma
 * regra que a calculadora anexada ao chamado ("A última parcela é ajustada
 * automaticamente para quitar o saldo remanescente, sem sobra").
 *
 * Sem esse ajuste, 24 arredondamentos de centavo deixam um resíduo que vira
 * uma 25ª cobrança de R$ 0,03 — ou, pior, uma quitação que não quita.
 */
export function montarTabela(
  valorFinanciadoCentavos: number,
  taxaMensal: number,
  n: number,
  parcelaBaseCentavos: number,
  primeiroVencimento: string,
): LinhaParcela[] {
  const linhas: LinhaParcela[] = [];
  let saldo = valorFinanciadoCentavos;

  for (let k = 1; k <= n; k++) {
    const juros = Math.round(saldo * taxaMensal);
    const ultima = k === n;
    // Na última, a parcela é o saldo + o juro do mês: fecha em zero, sempre.
    const valor = ultima ? saldo + juros : Math.min(parcelaBaseCentavos, saldo + juros);
    const amortizacao = valor - juros;
    const saldoFinal = saldo - amortizacao;
    const dataVencimento = somarMeses(primeiroVencimento, k - 1);
    linhas.push({
      numeroParcela: k,
      saldoInicialCentavos: saldo,
      jurosCentavos: juros,
      amortizacaoCentavos: amortizacao,
      saldoFinalCentavos: saldoFinal,
      valorCentavos: valor,
      dataVencimento,
      mesReferencia: mesReferenciaDe(dataVencimento),
    });
    saldo = saldoFinal;
    if (saldo <= 0) break;
  }

  return linhas;
}

/** Composição da dívida, sem financiamento. Isolada porque o termo e a tela
 *  precisam dela mesmo antes de o analista escolher entrada e prazo. */
export function comporDivida(p: Pick<ParametrosAcordo, 'faturas' | 'encargos' | 'saldo' | 'outrosDebitos' | 'dataBase'>) {
  const multaPct = (p.encargos?.multaPercent ?? 0) / 100;
  const moraPct = (p.encargos?.moraMensalPercent ?? 0) / 100;

  const detalheFaturas: DetalheFatura[] = p.faturas.map((f) => {
    const valorCentavos = paraCentavos(f.valor);
    // Mora só sobre o que está REALMENTE vencido na data-base. Fatura em aberto
    // dentro do prazo entra no acordo pelo valor de face.
    const diasAtraso = f.vencimento ? Math.max(0, diasEntre(f.vencimento, p.dataBase)) : 0;
    const emAtraso = diasAtraso > 0;
    const multaCentavos = emAtraso ? Math.round(valorCentavos * multaPct) : 0;
    // Pro rata die sobre mês comercial de 30 dias — é o que a Cláusula 5ª do
    // termo promete ao cliente, então é o que a conta tem que fazer.
    const moraCentavos = emAtraso ? Math.round(valorCentavos * moraPct * (diasAtraso / 30)) : 0;
    return {
      id: f.id,
      valorCentavos,
      diasAtraso,
      multaCentavos,
      moraCentavos,
      totalCentavos: valorCentavos + multaCentavos + moraCentavos,
    };
  });

  const valorFaturasCentavos = detalheFaturas.reduce((s, d) => s + d.valorCentavos, 0);
  const multaTotalCentavos = detalheFaturas.reduce((s, d) => s + d.multaCentavos, 0);
  const moraTotalCentavos = detalheFaturas.reduce((s, d) => s + d.moraCentavos, 0);

  const valorSaldoBrutoCentavos = p.saldo
    ? Math.round(paraCentavos(p.saldo.kwh * p.saldo.tarifa))
    : 0;
  const descontoSaldoCentavos = p.saldo
    ? Math.round(valorSaldoBrutoCentavos * ((p.saldo.descontoPercent ?? 0) / 100))
    : 0;
  const valorSaldoLiquidoCentavos = valorSaldoBrutoCentavos - descontoSaldoCentavos;

  const detalheOutrosDebitos: DetalheOutroDebito[] = (p.outrosDebitos || []).map((d) => {
    const valorCentavos = paraCentavos(d.valor);
    if (valorCentavos < 0) throw new ErroCalculoAcordo('O valor de um débito não pode ser negativo.');
    return { descricao: String(d.descricao ?? '').trim(), valorCentavos };
  });
  const outrosDebitosCentavos = detalheOutrosDebitos.reduce((s, d) => s + d.valorCentavos, 0);

  const dividaBrutaCentavos =
    valorFaturasCentavos + multaTotalCentavos + moraTotalCentavos + valorSaldoLiquidoCentavos + outrosDebitosCentavos;

  return {
    detalheFaturas,
    valorFaturasCentavos,
    multaTotalCentavos,
    moraTotalCentavos,
    valorSaldoBrutoCentavos,
    descontoSaldoCentavos,
    valorSaldoLiquidoCentavos,
    detalheOutrosDebitos,
    outrosDebitosCentavos,
    dividaBrutaCentavos,
  };
}

/**
 * Desconto sobre a dívida bruta, em centavos. Zero quando não há desconto.
 *
 * Tem que sobrar dívida: a entrada é obrigatória e sai do que resta depois do
 * desconto. Desconto de 100% não é acordo, é perdão — e não passa por esta tela.
 */
function descontoEmCentavos(d: DescontoConfig | null | undefined, dividaBrutaCentavos: number): number {
  if (!d) return 0;
  let centavos = 0;
  if (d.valor !== undefined && d.valor !== null) {
    centavos = paraCentavos(d.valor);
  } else if (d.percentual !== undefined && d.percentual !== null && Number.isFinite(d.percentual)) {
    centavos = Math.round(dividaBrutaCentavos * (d.percentual / 100));
  }
  if (centavos < 0) throw new ErroCalculoAcordo('O desconto não pode ser negativo.');
  if (centavos >= dividaBrutaCentavos) {
    throw new ErroCalculoAcordo(
      `O desconto (${formatarBRL(centavos)}) tem que ser menor que a dívida (${formatarBRL(dividaBrutaCentavos)}): ` +
      'a entrada é obrigatória e sai do que sobra.',
    );
  }
  return centavos;
}

/** Cálculo completo do acordo. Lança `ErroCalculoAcordo` com mensagem de
 *  operador (não de programador) em toda entrada inviável. */
export function calcularAcordo(p: ParametrosAcordo): ResultadoAcordo {
  const divida = comporDivida(p);

  if (divida.dividaBrutaCentavos <= 0) {
    throw new ErroCalculoAcordo('Não há valor a parcelar. Selecione faturas, informe um saldo de energia ou um outro débito.');
  }

  const descontoDividaCentavos = descontoEmCentavos(p.desconto, divida.dividaBrutaCentavos);
  const dividaNegociadaCentavos = divida.dividaBrutaCentavos - descontoDividaCentavos;
  const comDesconto = {
    ...divida,
    descontoDividaCentavos,
    descontoDividaPercentual: (descontoDividaCentavos / divida.dividaBrutaCentavos) * 100,
    dividaNegociadaCentavos,
  };

  // Entrada: valor tem precedência sobre percentual (é o que o analista digitou
  // por último na tela espelhada).
  let entradaCentavos: number;
  if (p.entrada.valor !== undefined && p.entrada.valor !== null) {
    entradaCentavos = paraCentavos(p.entrada.valor);
  } else if (p.entrada.percentual !== undefined && p.entrada.percentual !== null) {
    entradaCentavos = Math.round(dividaNegociadaCentavos * (p.entrada.percentual / 100));
  } else {
    entradaCentavos = 0;
  }

  if (entradaCentavos <= 0) {
    throw new ErroCalculoAcordo('A entrada é obrigatória e deve ser maior que zero.');
  }
  if (entradaCentavos > dividaNegociadaCentavos) {
    throw new ErroCalculoAcordo(
      `A entrada (${formatarBRL(entradaCentavos)}) não pode ser maior que a dívida` +
      `${descontoDividaCentavos > 0 ? ' com desconto' : ''} (${formatarBRL(dividaNegociadaCentavos)}).`,
    );
  }

  const taxaMensal = p.taxaJurosMensalPercent / 100;
  if (taxaMensal < 0) throw new ErroCalculoAcordo('A taxa de juros não pode ser negativa.');

  const valorFinanciadoCentavos = dividaNegociadaCentavos - entradaCentavos;

  // Entrada quitando tudo: acordo à vista. Válido — e não pode explodir.
  if (valorFinanciadoCentavos === 0) {
    return {
      ...comDesconto,
      entradaCentavos,
      entradaPercentual: 100,
      vencimentoEntrada: p.vencimentoEntrada,
      valorFinanciadoCentavos: 0,
      taxaJurosMensalPercent: p.taxaJurosMensalPercent,
      parcelas: [],
      numeroParcelas: 0,
      jurosTotalCentavos: 0,
      totalAcordoCentavos: entradaCentavos,
      custoFinanciamentoCentavos: 0,
    };
  }

  let n: number;
  let parcelaBase: number;
  if (p.parcelamento.modo === 'por_numero') {
    n = Math.trunc(p.parcelamento.numeroParcelas);
    if (n <= 0) throw new ErroCalculoAcordo('O número de parcelas deve ser maior que zero.');
    if (n > MAX_PARCELAS) throw new ErroCalculoAcordo(`O número de parcelas não pode passar de ${MAX_PARCELAS}.`);
    parcelaBase = parcelaPrice(valorFinanciadoCentavos, taxaMensal, n);
  } else {
    parcelaBase = paraCentavos(p.parcelamento.valorParcela);
    n = numeroParcelasPorValor(valorFinanciadoCentavos, taxaMensal, parcelaBase);
  }

  const parcelas = montarTabela(valorFinanciadoCentavos, taxaMensal, n, parcelaBase, p.primeiroVencimento);

  const jurosTotalCentavos = parcelas.reduce((s, l) => s + l.jurosCentavos, 0);
  const somaParcelas = parcelas.reduce((s, l) => s + l.valorCentavos, 0);
  const totalAcordoCentavos = entradaCentavos + somaParcelas;

  // Invariante de fechamento: a tabela TEM que quitar o financiado. Se não
  // quitar, algo na aritmética mudou e é melhor explodir aqui do que emitir
  // boleto errado. Nunca deve disparar — é uma rede, não um caminho.
  const saldoFinal = parcelas.length ? parcelas[parcelas.length - 1].saldoFinalCentavos : 0;
  if (saldoFinal !== 0) {
    throw new ErroCalculoAcordo(
      `Erro interno no fechamento do acordo: sobrou ${formatarBRL(saldoFinal)} de saldo devedor. Não prossiga — avise a TI.`,
    );
  }
  if (somaParcelas - jurosTotalCentavos !== valorFinanciadoCentavos) {
    throw new ErroCalculoAcordo(
      'Erro interno: a soma das amortizações não fecha com o valor financiado. Não prossiga — avise a TI.',
    );
  }

  return {
    ...comDesconto,
    entradaCentavos,
    entradaPercentual: (entradaCentavos / dividaNegociadaCentavos) * 100,
    vencimentoEntrada: p.vencimentoEntrada,
    valorFinanciadoCentavos,
    taxaJurosMensalPercent: p.taxaJurosMensalPercent,
    parcelas,
    numeroParcelas: parcelas.length,
    jurosTotalCentavos,
    totalAcordoCentavos,
    custoFinanciamentoCentavos: totalAcordoCentavos - dividaNegociadaCentavos,
  };
}
